import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Client } from '../../../types/client';

interface ClientCardProps {
  client: Client;
  onUpdate: (client: Client) => void;
  onDelete: (id: string) => void;
  initialExpanded?: boolean;
  highlightOnMount?: boolean;
}

export const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onUpdate,
  onDelete,
  initialExpanded = false,
  highlightOnMount = false
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [isHighlighted, setIsHighlighted] = useState(highlightOnMount);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedClient, setEditedClient] = useState<Client>(client);
  const [originalClient, setOriginalClient] = useState<Client>(client);

  const handleFieldChange = (field: string, value: any) => {
    if (isEditMode) {
      setEditedClient({ ...editedClient, [field]: value } as Client);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setOriginalClient(client);
    setEditedClient(client);
  };

  const handleSave = () => {
    onUpdate(editedClient);
    setIsEditMode(false);
    setOriginalClient(editedClient);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedClient(originalClient);
  };

  const handleDelete = () => {
    const displayName =
      client.type === 'individual' ? client.name || 'this client' : client.retailerName || 'this retailer';
    if (window.confirm(`Are you sure you want to delete ${displayName}?`)) {
      onDelete(client.id);
    }
  };

  const createdDate = new Date(client.createdAt).toLocaleDateString();

  // Handle highlighting and scrolling when component mounts with highlightOnMount
  useEffect(() => {
    if (highlightOnMount) {
      // Scroll to this card
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Remove highlight after 3 seconds
      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [highlightOnMount]);

  // Sync editedClient when client prop changes externally
  useEffect(() => {
    if (!isEditMode) {
      setEditedClient(client);
      setOriginalClient(client);
    }
  }, [client, isEditMode]);

  return (
    <div
      ref={cardRef}
      style={{
        border: `2px solid ${isHighlighted ? '#2196F3' : '#ddd'}`,
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        boxShadow: isHighlighted ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none'
      }}
    >
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '16px',
          backgroundColor: client.type === 'retailer' ? '#e3f2fd' : '#f5f5f5',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: expanded ? '1px solid #ddd' : 'none'
        }}
      >
        <div style={{ flex: 1 }}>
          {client.type === 'individual' ? (
            <>
              <Link
                to={`/clients/${client.id}`}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'inline-block'
                }}
                onClick={e => e.stopPropagation()}
              >
                <h3
                  style={{
                    margin: '0 0 8px 0',
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textDecorationColor: 'transparent',
                    transition: 'text-decoration-color 0.2s'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.textDecorationColor = '#4CAF50')}
                  onMouseLeave={e => (e.currentTarget.style.textDecorationColor = 'transparent')}
                >
                  {client.name || 'New Client'}
                </h3>
              </Link>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                <span>{client.email || 'No email'}</span>
                {client.phone && (
                  <>
                    <span style={{ margin: '0 12px' }}>•</span>
                    <span>{client.phone}</span>
                  </>
                )}
                <span style={{ margin: '0 12px' }}>•</span>
                <span>Added {createdDate}</span>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Link
                  to={`/clients/${client.id}`}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'inline-block'
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationColor: 'transparent',
                      transition: 'text-decoration-color 0.2s'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.textDecorationColor = '#4CAF50')}
                    onMouseLeave={e => (e.currentTarget.style.textDecorationColor = 'transparent')}
                  >
                    {client.retailerName || 'New Retailer'}
                  </h3>
                </Link>
                <span
                  style={{
                    fontSize: '0.75rem',
                    color: '#2196F3',
                    backgroundColor: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                >
                  RETAILER
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '4px' }}>
                Contact: {client.contactName || 'No contact name'}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                <span>{client.email || 'No email'}</span>
                {client.phone && (
                  <>
                    <span style={{ margin: '0 12px' }}>•</span>
                    <span>{client.phone}</span>
                  </>
                )}
                <span style={{ margin: '0 12px' }}>•</span>
                <span>Added {createdDate}</span>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.5rem', color: '#666' }}>{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: '20px' }}>
          {/* Client Info */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px'
            }}
          >
            {client.type === 'individual' ? (
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Name</label>
                <input
                  type="text"
                  value={editedClient.name}
                  onChange={e => handleFieldChange('name', e.target.value)}
                  disabled={!isEditMode}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                    cursor: isEditMode ? 'text' : 'not-allowed'
                  }}
                />
              </div>
            ) : (
              <>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Retailer Name</label>
                  <input
                    type="text"
                    value={editedClient.retailerName}
                    onChange={e => handleFieldChange('retailerName', e.target.value)}
                    disabled={!isEditMode}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                      cursor: isEditMode ? 'text' : 'not-allowed'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Contact Name</label>
                  <input
                    type="text"
                    value={editedClient.contactName}
                    onChange={e => handleFieldChange('contactName', e.target.value)}
                    disabled={!isEditMode}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                      cursor: isEditMode ? 'text' : 'not-allowed'
                    }}
                  />
                </div>
              </>
            )}

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Email</label>
              <input
                type="email"
                value={editedClient.email}
                onChange={e => handleFieldChange('email', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Phone</label>
              <input
                type="tel"
                value={editedClient.phone}
                onChange={e => handleFieldChange('phone', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Address</label>
              <input
                type="text"
                value={editedClient.address}
                onChange={e => handleFieldChange('address', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Preferences</label>
              <textarea
                value={editedClient.preferences}
                onChange={e => handleFieldChange('preferences', e.target.value)}
                disabled={!isEditMode}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Notes</label>
              <textarea
                value={editedClient.notes}
                onChange={e => handleFieldChange('notes', e.target.value)}
                disabled={!isEditMode}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              borderTop: '1px solid #ddd',
              paddingTop: '16px'
            }}
          >
            <button
              onClick={handleDelete}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Delete Client
            </button>

            <div style={{ display: 'flex', gap: '12px' }}>
              {isEditMode ? (
                <>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#757575',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Save
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEdit}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
